import { formatDate } from '@clim/client-core';
import { useEffect, useState } from 'react';
import { Empty, ErrorBox, Field, Input, Select } from '../../components/ui';
import { useApi, useMutation } from './common';

interface Data {
  provider: 'brevo' | 'none';
  enabled: boolean;
  smsSender: string;
  messages: {
    id: string;
    channel: 'email' | 'sms';
    purpose: string;
    to: string;
    subject: string | null;
    preview: string;
    status: 'queued' | 'sent' | 'failed' | 'disabled';
    attempts: number;
    error: string | null;
    createdAt: string;
    sentAt: string | null;
  }[];
}
const STATUS: Record<Data['messages'][number]['status'], [string, string]> = {
  queued: ['En file', 'info'],
  sent: ['Envoyé', 'success'],
  failed: ['Échec', 'danger'],
  disabled: ['Non envoyé (service inactif)', 'neutral'],
};

/**
 * Service d’envoi (e-mails et SMS) : mot de passe oublié, demandes d’avis, relances des documents des sous-traitants.
 * La clé du fournisseur se règle dans les variables d’environnement du serveur, jamais dans l’application.
 */
export function MessagingAdmin() {
  const res = useApi<Data>('/v1/admin/messaging');
  const m = useMutation();
  const [sender, setSender] = useState('');
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [to, setTo] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  useEffect(() => {
    if (res.data) setSender(res.data.smsSender);
  }, [res.data]);
  if (!res.data) return <ErrorBox message={res.error} />;
  const d = res.data;
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setInfo(null);
    try {
      await fn();
      setInfo(ok);
      res.reload();
    } catch {
      /* shown by m.error */
    }
  };
  return (
    <div>
      <div className="card">
        <h2>E-mails & SMS</h2>
        {d.enabled ? (
          <p>
            Service d’envoi : <strong>{d.provider === 'brevo' ? 'Brevo' : d.provider}</strong>{' '}
            <span className="badge tone-success">actif</span>
          </p>
        ) : (
          <p className="warning-box">
            Aucun service d’envoi n’est configuré : les messages sont enregistrés ci-dessous mais ne partent
            pas. Les demandes d’avis restent possibles depuis votre téléphone (SMS, WhatsApp, e-mail). La mise
            en service se fait par l’hébergeur (voir la notice d’installation, rubrique « E-mails et SMS »).
          </p>
        )}
        <p className="muted">
          Utilisés pour : mot de passe oublié, demandes d’avis envoyées automatiquement, relances des
          documents des sous-traitants.
        </p>
      </div>
      <ErrorBox message={m.error} />
      {info && <p role="status">{info}</p>}
      <div className="card form">
        <h2>Expéditeur des SMS</h2>
        <Field label="Nom affiché (11 caractères, lettres et chiffres)">
          <Input value={sender} maxLength={11} onChange={(e) => setSender(e.target.value)} />
        </Field>
        <button
          className="btn"
          onClick={() =>
            void run(
              () => m.run('PATCH', '/v1/admin/messaging', { smsSender: sender }),
              'Expéditeur enregistré.',
            )
          }
        >
          Enregistrer
        </button>
      </div>
      <div className="card form">
        <h2>Envoyer un test</h2>
        <div className="row-inline">
          <Field label="Canal">
            <Select value={channel} onChange={(e) => setChannel(e.target.value as 'email' | 'sms')}>
              <option value="email">E-mail</option>
              <option value="sms">SMS</option>
            </Select>
          </Field>
          <Field label={channel === 'email' ? 'Adresse e-mail' : 'Numéro de mobile'}>
            <Input value={to} onChange={(e) => setTo(e.target.value)} aria-label="Destinataire du test" />
          </Field>
        </div>
        <button
          className="btn"
          disabled={to.trim().length < 5}
          onClick={() =>
            void run(
              () => m.run('POST', '/v1/admin/messaging/test', { channel, to }),
              'Message de test mis en file d’envoi.',
            )
          }
        >
          Envoyer le test
        </button>
      </div>
      <div className="card">
        <div className="card-head">
          <h2>Journal des envois</h2>
          <button className="btn small" onClick={res.reload}>
            Actualiser
          </button>
        </div>
        {!d.messages.length ? (
          <Empty>Aucun envoi pour le moment.</Empty>
        ) : (
          <table className="table compact" aria-label="Journal des envois">
            <thead>
              <tr>
                <th>Date</th>
                <th>Objet</th>
                <th>Destinataire</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {d.messages.map((x) => (
                <tr key={x.id}>
                  <td className="nowrap">{formatDate(x.createdAt)}</td>
                  <td>
                    {x.channel === 'sms' ? 'SMS' : 'E-mail'} · {x.purpose}
                    <div className="muted">
                      {x.subject ? `${x.subject} — ` : ''}
                      {x.preview}
                    </div>
                  </td>
                  <td>{x.to}</td>
                  <td>
                    <span className={`badge tone-${STATUS[x.status][1]}`}>{STATUS[x.status][0]}</span>
                    {x.error && x.status !== 'sent' && (
                      <div className="muted">
                        {x.error}
                        {x.attempts > 1 ? ` (${x.attempts} essais)` : ''}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
